import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useIsAdmin, useIsDirector, useIsManagement, useIsStaff } from '@/hooks/useAuth';
import { useAppConfig } from '@/hooks/useAppConfig';

const COLLAPSED_WIDTH = 54;
const EXPANDED_WIDTH  = 220;
const STORAGE_KEY = 'sidebar:collapsed';

const baseNav: React.CSSProperties = {
  height: '100vh',
  background: '#1a1a2e',
  color: '#ccc',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  position: 'relative',
  transition: 'width 0.18s ease',
};

const linkStyle: React.CSSProperties = {
  display: 'block',
  padding: '10px 24px',
  color: '#bbb',
  textDecoration: 'none',
  fontSize: 14,
  borderLeftWidth: 3,
  borderLeftStyle: 'solid',
  borderLeftColor: 'transparent',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexShrink: 0,
  background: 'transparent',
};

const activeLinkStyle: React.CSSProperties = {
  ...linkStyle,
  color: '#fff',
  background: 'rgba(255,255,255,0.07)',
  borderLeftColor: 'var(--brand)',
};

const groupLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  color: '#555',
  padding: '16px 24px 4px',
  textTransform: 'uppercase',
  flexShrink: 0,
};

const collapseBtn: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: -12,
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '1px solid #2a2a45',
  background: '#1a1a2e',
  color: '#bbb',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  lineHeight: 1,
  padding: 0,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  zIndex: 10,
};

function SideLink({ to, label, collapsed }: { to: string; label: string; collapsed: boolean }) {
  // When collapsed, show the first letter as an icon-ish marker and use title for hover tooltip.
  const shortLabel = label.charAt(0);
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        ...(isActive ? activeLinkStyle : linkStyle),
        padding: collapsed ? '10px 0' : '10px 24px',
        textAlign: collapsed ? 'center' : 'left',
        fontSize: collapsed ? 13 : 14,
        fontWeight: collapsed ? 600 : 400,
      })}
    >
      {collapsed ? shortLabel : label}
    </NavLink>
  );
}

export function Sidebar() {
  const isAdmin = useIsAdmin();
  const isDirector = useIsDirector();
  const isManagement = useIsManagement();
  const isStaff = useIsStaff();
  const appConfig = useAppConfig();
  const [logoFailed, setLogoFailed] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const navStyle: React.CSSProperties = {
    ...baseNav,
    width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
  };

  return (
    <nav style={navStyle}>
      <style>{`.sidebar-scroll::-webkit-scrollbar { display: none; }`}</style>
      <button
        onClick={() => setCollapsed((v) => !v)}
        style={collapseBtn}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? '›' : '‹'}
      </button>

      <div
        className="sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px 0',
          display: 'flex',
          flexDirection: 'column',
          scrollbarWidth: 'none',         // Firefox
          msOverflowStyle: 'none' as any, // IE/Edge legacy
        }}
      >
      <div style={{ padding: collapsed ? '0 8px 24px' : '0 24px 24px', flexShrink: 0 }}>
        {!logoFailed ? (
          <img
            src="/logo.png"
            alt="Logo"
            onError={() => setLogoFailed(true)}
            style={{ width: '100%', objectFit: 'contain' }}
          />
        ) : (
          !collapsed && <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Inventory & Quoting</span>
        )}
      </div>

      <SideLink to="/dashboard" label="Dashboard" collapsed={collapsed} />
      <SideLink to="/calendar"  label="Calendar"  collapsed={collapsed} />
      <SideLink to="/warehouse" label="Warehouse" collapsed={collapsed} />

      {!collapsed && <div style={groupLabel}>Jobs</div>}
      {isManagement && <SideLink to="/projects" label="Projects" collapsed={collapsed} />}
      <SideLink to="/jobs" label="Jobs" collapsed={collapsed} />
      {isManagement && <SideLink to="/clients" label="Clients" collapsed={collapsed} />}

      {!collapsed && <div style={groupLabel}>Inventory</div>}
      <SideLink to="/inventory" label="Equipment" collapsed={collapsed} />
      <SideLink to="/admin/test-tag" label="Test & Tag" collapsed={collapsed} />

      {!collapsed && <div style={groupLabel}>Catalogues</div>}
      {isManagement && <SideLink to="/catalogue/crew" label="Crew" collapsed={collapsed} />}
      <SideLink to="/catalogue/labour"    label="Labour"    collapsed={collapsed} />
      <SideLink to="/catalogue/transport" label="Transport" collapsed={collapsed} />
      <SideLink to="/catalogue/other"     label="Other"     collapsed={collapsed} />
      <SideLink to="/catalogue/sales"     label="Sales"     collapsed={collapsed} />
      {!isStaff && <SideLink to="/catalogue/subhire" label="Sub-Hire" collapsed={collapsed} />}

      {/* The group opens at management for Analytics' sake — its sales tab is
          management+ — so everything else inside gates on isDirector itself. */}
      {isManagement && (
        <>
          {!collapsed && <div style={groupLabel}>Admin</div>}
          {isDirector && <SideLink to="/admin/categories" label="Categories" collapsed={collapsed} />}
          {isDirector && <SideLink to="/admin/data"       label="Data"       collapsed={collapsed} />}
          <SideLink to="/admin/analytics" label="Analytics" collapsed={collapsed} />
          {isAdmin && <SideLink to="/admin/users"         label="Users"        collapsed={collapsed} />}
          {isAdmin && <SideLink to="/admin/system"        label="System"       collapsed={collapsed} />}
          {isAdmin && <SideLink to="/admin/activity-log"  label="Activity Log" collapsed={collapsed} />}
          {isAdmin && appConfig?.devTools && (
            <SideLink to="/admin/dev-tools" label="Dev Tools" collapsed={collapsed} />
          )}
        </>
      )}
      </div>

      {appConfig?.version && (
        <div
          style={{
            flexShrink: 0,
            padding: collapsed ? '8px 4px' : '10px 24px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontSize: 10,
            color: '#6b6b80',
            textAlign: collapsed ? 'center' : 'left',
            letterSpacing: 0.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={`Version ${appConfig.version} · ${appConfig.environment}`}
        >
          v{appConfig.version}
          {!collapsed && appConfig.environment && appConfig.environment !== 'production' && (
            <span style={{ color: '#9333ea', fontWeight: 700 }}> · {appConfig.environment}</span>
          )}
        </div>
      )}
    </nav>
  );
}
